import { Box, Divider, Typography, useTheme } from '@mui/material';
import ActionButtons from 'components/action-buttons';
import Drawer from 'components/drawer';
import Paper from 'components/paper';

import Tooltip from 'components/tooltip';
import dayjs from 'dayjs';
import { ChevronRight, HelpCircle } from 'lucide-react';
import { PropsWithChildren, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import EditFeedInventory from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/edit-feed-inventory';
import FeedInventory from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/feed-inventory';
import FeedInventoryExplanation from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/feed-inventory-explanation';
import FeedInventoryLog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/feed-inventory-log';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { useUpdateOFDSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-update-ofd-settings';
import { UpdateManualInventoryInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { EventNameToTranslationKey } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

type Props = PropsWithChildren & {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

const FeedInventoryDialog: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const today = dayjs().format('YYYY-MM-DD');

  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();
  const { createInputSettingsEvent } = useGoogleTagManager();

  const { updateFeedInventory } = useUpdateOFDSettings();
  // #region STATES
  const [edit, setEdit] = useState(false);

  // #endregion STATES

  const defaultValues: UpdateManualInventoryInput = useMemo(() => {
    return {
      inventory: ofdSettings?.inventoryExplanation.startingInventory ?? 0,
      afterDelivery: ofdSettings?.manualInventory?.afterDelivery ?? null,
    };
  }, [ofdSettings]);

  const { control, handleSubmit, setValue, getValues } = useForm<UpdateManualInventoryInput>({
    defaultValues: defaultValues,
  });

  if (!ofdSettings) return null;

  const { inventoryExplanation } = ofdSettings;

  // #region HANDLERS
  const handleEdit = (edited: boolean) => setEdit(edited);

  const handleCancel = () => setEdit(false);

  const handleUpdateFeedInventory = (data: UpdateManualInventoryInput) => {
    updateFeedInventory.mutate(data, {
      onSuccess: (data) => {
        if (ofdSettings) {
          let inventoryMeasured: '' | 'before delivery' | 'after delivery' = '';
          const afterDelivery = getValues('afterDelivery');

          switch (afterDelivery) {
            case true:
              inventoryMeasured = 'after delivery';
              break;
            case false:
              inventoryMeasured = 'before delivery';
              break;
            case null:
            default:
              break;
          }

          createInputSettingsEvent('save_feed_inventory', {
            oldValue1: String(ofdSettings.inventoryExplanation.startingInventory),
            newValue1: String(getValues('inventory')),
            section: 'modal',
            consumptionRate: String(ofdSettings.currentConsumptionRate),
            inventoryMeasured: inventoryMeasured,
          });
        }
      },
    });
  };
  // #endregion HANDLERS

  return (
    <Paper transparent sx={{ borderRadius: 2, mb: 6 }}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <Typography display={'flex'}>
            <FormattedMessage id={`${PAGE_NAME_ID}.cards.startingInventory`} />
          </Typography>
          <Tooltip
            title={
              <>
                <Typography fontWeight={'bold'} mb={2}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.tooltipContent`} />
                  <FormattedDate value={today} year="numeric" month="long" day="2-digit" />
                </Typography>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.cards.startingInventory`} />:
                  </Typography>
                  <FormattedNumber
                    value={inventoryExplanation.startingInventory}
                    unit={'kilogram'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Box>
                {ofdSettings?.inventoryExplanation.events.map((event, idx) => (
                  <Box display={'flex'} justifyContent={'space-between'} key={idx}>
                    <Typography>
                      <FormattedMessage
                        id={`${PAGE_NAME_ID}.feedInventory.tooltip.events.${EventNameToTranslationKey[event.event]}`}
                      />
                      :
                    </Typography>
                    <FormattedNumber
                      value={event.adjustment}
                      unit={'kilogram'}
                      style="unit"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </Box>
                )) ?? 0}
                <Divider sx={{ mt: 2, mb: 2 }} color="#809198" />
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.cards.endingInventory`} />:
                  </Typography>
                  <FormattedNumber
                    value={inventoryExplanation.endingInventory}
                    unit={'kilogram'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Box>
              </>
            }
            placement={'top'}>
            <HelpCircle size={18} />
          </Tooltip>
        </Box>
      </Box>
      <Box mt={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'}>
          <Typography fontSize={23} fontWeight={'bold'}>
            <FormattedNumber
              value={inventoryExplanation.startingInventory}
              unit={'kilogram'}
              style="unit"
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          </Typography>
          <Typography>
            <Box ml={2}>
              <FormattedNumber
                value={inventoryExplanation?.startingPercentOfCapacity * 100}
                unit={'percent'}
                style="unit"
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
              &nbsp;
              <FormattedMessage id={`${PAGE_NAME_ID}.cards.capacity`} />
            </Box>
          </Typography>
        </Box>
        <Drawer
          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.drawer.feedInventory` })}
          actionButtons={
            <ActionButtons
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSubmit(handleUpdateFeedInventory)}
              isLoading={updateFeedInventory.isPending}
            />
          }
          button={
            <Typography sx={{ cursor: 'pointer' }} display={'flex'} gap={1} alignItems={'center'}>
              <FormattedMessage id={`${PAGE_NAME_ID}.cards.buttons.openInventory`} />
              <ChevronRight size={18} />
            </Typography>
          }>
          <Box width={750}>
            <Box>
              {edit ? (
                <EditFeedInventory isDialog control={control} setValue={setValue} />
              ) : (
                <Box display={'flex'} flexDirection={'column'} gap={4}>
                  <FeedInventory isDialog />
                  <FeedInventoryExplanation />
                  <FeedInventoryLog />
                </Box>
              )}
            </Box>
          </Box>
        </Drawer>
      </Box>
    </Paper>
  );
};

export default FeedInventoryDialog;
